import { useEffect, useState } from "react";
import { useGlobalReducer } from "../../../GlobalContext";
import { LookupOption } from "../fields/LookupField.types";

const NoOptions: LookupOption[] = [];

// An entity's attributes don't change within a session, so each is read once.
const attributes = new Map<string, Promise<LookupOption[]>>();

async function loadAttributes(dispatch: unknown, entityName: string) {
  if (!attributes.has(entityName)) {
    // Loaded on first use, so the service isn't pulled into every page that has the editor.
    const { GetEntityAttributes } = await import("../../api/entityService");
    const loading = GetEntityAttributes(dispatch, entityName).then(
      (response: { data?: { Name: string; DisplayName: string }[] }) => {
        if (!Array.isArray(response?.data)) {
          throw new Error("The attributes could not be read");
        }

        return response.data.map((attribute) => ({
          id: attribute.Name,
          displayName: attribute.DisplayName,
        }));
      }
    );

    attributes.set(entityName, loading);
    loading.catch(() => attributes.delete(entityName));
  }

  return attributes.get(entityName) as Promise<LookupOption[]>;
}

/** Only for tests - the attributes are otherwise held for the session. */
export const clearEntityAttributes = () => attributes.clear();

/** The attributes of an entity as options, for choosing one to merge or link to. */
export function useEntityAttributes(entityName: string | null) {
  const dispatch = useGlobalReducer();
  const [options, setOptions] = useState<LookupOption[]>(NoOptions);
  const [loading, setLoading] = useState(Boolean(entityName));
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    if (!entityName) {
      setOptions(NoOptions);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setFailed(false);
    loadAttributes(dispatch, entityName)
      .then((loaded) => !cancelled && setOptions(loaded))
      .catch(() => {
        if (!cancelled) {
          setOptions(NoOptions);
          setFailed(true);
        }
      })
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [dispatch, entityName]);

  return { options, loading, failed };
}
